import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { ReactNode, useMemo, useRef, useEffect, useState } from 'react';
import { FieldValues, UseFormReturn, useFieldArray } from 'react-hook-form';

export interface FieldConfig {
        /** Unique identifier for the field */
        name: string;
        /** Render function for the field */
        render: (params: {
                form: UseFormReturn<any>;
                path: string;
                index: number;
                itemValue: any;
                /** True if this is a new uninitialized item (key field is empty) */
                isNewItem: boolean;
                /** Remove/delete this item from the list */
                onRemove: () => void;
        }) => ReactNode;
        /** If true, this is the "key" field used to detect initialization */
        isKeyField?: boolean;
        /** Column span class for the field wrapper (e.g., 'md:col-span-2', 'col-span-full') */
        colSpan?: string;
}

export type FieldListVariant = 'cards' | 'single-panel';
export type SinglePanelLayout = 'stacked' | 'inline-wrap';

export interface FieldListProps<
        TFieldValues extends FieldValues = any
> {
        /** React Hook Form instance */
        form: UseFormReturn<TFieldValues>;
        /** Name of the field array in the form (e.g., 'bankDetails', 'beneficiaries') */
        name: string;
        /** Configuration for each field in the row */
        fields: FieldConfig[];
        /** Default values for a new row */
        defaultItem: any;
        /** Title of the section */
        title: string;
        /** Description text shown below the title */
        description?: string;
        /** Text shown when list is empty */
        emptyMessage?: string;
        /** Additional class names for the container */
        className?: string;
        /** Name of the ID field for soft-delete tracking (e.g., 'bankDetailsId') */
        idField?: string;
        /** Name of the isDeleted flag field */
        deletedField?: string;
        /**
         * When true, always performs hard-delete (removes item from array) regardless of whether
         * the item has an ID. Use this when the backend expects the full list of items on update
         * and handles deletions by comparing against existing items.
         * Default: false (uses soft-delete for items with IDs)
         */
        forceHardDelete?: boolean;
        /** Optional render function for header actions (appears next to delete button) */
        renderHeaderActions?: (params: {
                form: UseFormReturn<any>;
                path: string;
                index: number;
                isInitialized: boolean;
        }) => ReactNode;
        /** Label for the add button */
        addButtonLabel?: string;
        /** Custom grid class for items container (default: 'space-y-6' for vertical stack) */
        itemsGridClassName?: string;
        /** Visual layout variant */
        variant?: FieldListVariant;
        /** Layout mode when `variant` is `single-panel` */
        singlePanelLayout?: SinglePanelLayout;
        /** Width class for each item when `singlePanelLayout` is `inline-wrap` */
        singlePanelItemWidthClassName?: string;
        /** When true, hides the external delete button (use when delete is handled inline, e.g. inside an autocomplete) */
        hideDeleteButton?: boolean;
}

/**
 * Manual field list component with explicit add button
 */
export function FieldList<TFieldValues extends FieldValues = any>({
        form,
        name,
        fields,
        defaultItem,
        title,
        description,
        emptyMessage = 'No items added yet.',
        className = '',
        idField,
        deletedField = 'isDeleted',
        forceHardDelete = false,
        renderHeaderActions,
        addButtonLabel = 'Add item',
        itemsGridClassName,
        variant = 'cards',
        singlePanelLayout = 'stacked',
        singlePanelItemWidthClassName = 'w-[360px] max-w-full',
        hideDeleteButton = false
}: FieldListProps<TFieldValues>) {
        const fieldArray = useFieldArray({
                control: form.control,
                name: name as any
        });

        const values = (form.watch(name as any) ?? []) as any[];

        // Filter out deleted items for display
        const visibleItems = useMemo(
                () =>
                        fieldArray.fields
                                .map((field, index) => ({
                                        field,
                                        index,
                                        state: values[index]
                                }))
                                .filter(item => !item.state?.[deletedField]),
                [fieldArray.fields, values, deletedField]
        );

        const keyField = fields.find(f => f.isKeyField);

        // Use state to track user-added items so it triggers re-render
        const [userAddedItemIds, setUserAddedItemIds] = useState<Set<string>>(new Set());
        const newItemIndexRef = useRef<number | null>(null); // Track the index of newly added item for auto-focus
        const itemContainersRef = useRef<Map<number, HTMLDivElement>>(new Map()); // Refs to item containers for focus
        // Track when we're about to add an item via user action - stores the expected new length
        const pendingAddLengthRef = useRef<number | null>(null);

        // Synchronously check if the last item should be marked as user-added
        // This runs during render, before the motion.div is created
        if (pendingAddLengthRef.current !== null && fieldArray.fields.length === pendingAddLengthRef.current) {
                const lastField = fieldArray.fields[fieldArray.fields.length - 1];
                if (lastField && !userAddedItemIds.has(lastField.id)) {
                        // Add to local set immediately (will be in sync with state after next render)
                        userAddedItemIds.add(lastField.id);
                        // Also trigger state update for consistency
                        setUserAddedItemIds(new Set(userAddedItemIds));
                }
                pendingAddLengthRef.current = null;
        }

        // Auto-focus the first input of a newly added item
        useEffect(() => {
                if (newItemIndexRef.current !== null) {
                        const newIndex = newItemIndexRef.current;
                        // Small delay to ensure the DOM has been updated and animations started
                        const timeoutId = setTimeout(() => {
                                const container = itemContainersRef.current.get(newIndex);
                                if (container) {
                                        // Find the first focusable input in the new item
                                        const firstInput = container.querySelector<HTMLElement>(
                                                'input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'
                                        );
                                        if (firstInput) {
                                                firstInput.focus();
                                        }
                                }
                                newItemIndexRef.current = null;
                        }, 50);
                        return () => clearTimeout(timeoutId);
                }
        }, [visibleItems.length]); // Trigger when visible items change

        const handleAdd = () => {
                // Track the index of the new item (it will be added at the end)
                newItemIndexRef.current = fieldArray.fields.length;
                // Mark that we're about to add an item via user action - track expected length
                pendingAddLengthRef.current = fieldArray.fields.length + 1;
                fieldArray.append({ ...defaultItem });
        };

        const handleRemove = (originalIndex: number) => {
                const current = values[originalIndex];

                // Always hard-delete if forceHardDelete is enabled
                if (forceHardDelete) {
                        // Use form.setValue directly for reliable removal
                        const currentValues = form.getValues(name as any) as any[];
                        const newValues = currentValues.filter((_: any, i: number) => i !== originalIndex);
                        form.setValue(name as any, newValues as any, { shouldValidate: true });
                        return;
                }

                // Soft-delete if item has an ID (exists in backend)
                if (idField && current?.[idField]) {
                        fieldArray.update(originalIndex, {
                                ...(current as object),
                                [deletedField]: true
                        } as any);
                        return;
                }

                // Hard-delete if it's a new item (no ID yet)
                const currentValues = form.getValues(name as any) as any[];
                const newValues = currentValues.filter((_: any, i: number) => i !== originalIndex);
                form.setValue(name as any, newValues as any, { shouldValidate: true });
        };

        const handleClearAll = () => {
                const currentValues = form.getValues(name as any) as any[];
                if (!currentValues || currentValues.length === 0) return;

                if (forceHardDelete || !idField) {
                        // Hard-delete all: clear the array
                        form.setValue(name as any, [] as any, { shouldValidate: true });
                } else {
                        // Mixed: soft-delete items with IDs, remove new items
                        const newValues = currentValues
                                .filter((item: any) => item[idField]) // Keep only items with IDs
                                .map((item: any) => ({ ...item, [deletedField]: true })); // Soft-delete them
                        form.setValue(name as any, newValues as any, { shouldValidate: true });
                }
                setUserAddedItemIds(new Set());
        };

        const isSinglePanel = variant === 'single-panel';
        const isSinglePanelInlineWrap =
                isSinglePanel && singlePanelLayout === 'inline-wrap';
        const itemsContainerClassName = isSinglePanel
                ? isSinglePanelInlineWrap
                        ? 'border rounded-lg bg-card p-2'
                        : `overflow-hidden border rounded-lg bg-card ${itemsGridClassName || ''}`
                : itemsGridClassName || 'space-y-6';
        const inlineWrapItemsClassName =
                itemsGridClassName || 'flex flex-wrap gap-2';

        const itemsContent = (
                <AnimatePresence initial={false}>
                        {visibleItems.map(({ field, index, state }) => {
                                const path = `${name}.${index}` as const;
                                const keyFieldValue = keyField
                                        ? state?.[keyField.name]
                                        : undefined;
                                const isInitialized = keyField
                                        ? Boolean(keyFieldValue)
                                        : true;

                                // Only animate items that were explicitly added by user via handleAdd
                                const isUserAddedItem = userAddedItemIds.has(field.id);

                                return (
                                        <motion.div
                                                key={field.id}
                                                ref={(el: HTMLDivElement | null) => {
                                                        if (el) {
                                                                itemContainersRef.current.set(index, el);
                                                        } else {
                                                                itemContainersRef.current.delete(index);
                                                        }
                                                }}
                                                initial={isUserAddedItem ? { opacity: 0, y: -5 } : false}
                                                animate={isUserAddedItem ? { opacity: 1, y: 0 } : undefined}
                                                exit={{ opacity: 0, y: -5 }}
                                                transition={isUserAddedItem ? { duration: 0.2 } : { duration: 0 }}
                                                layout={isUserAddedItem ? true : false}
                                                className={
                                                        isSinglePanel
                                                                ? isSinglePanelInlineWrap
                                                                        ? `${singlePanelItemWidthClassName} min-w-0`
                                                                        : 'px-3 py-2.5 border-b border-border/50 last:border-b-0'
                                                                : 'p-4 bg-white border rounded-lg shadow-sm dark:bg-slate-800 dark:border-slate-700'
                                                }
                                        >
                                                {/* Hidden fields for ID tracking */}
                                                <div className="hidden">
                                                        {/* Only render idField input if value exists - prevents empty string override */}
                                                        {idField && state?.[idField] && (
                                                                <input
                                                                        type="hidden"
                                                                        value={state[idField]}
                                                                        {...form.register(
                                                                                `${path}.${idField}` as any
                                                                        )}
                                                                />
                                                        )}
                                                        <input
                                                                type="hidden"
                                                                {...form.register(
                                                                        `${path}.${deletedField}` as any,
                                                                        {
                                                                                setValueAs: v => {
                                                                                        if (typeof v === 'boolean')
                                                                                                return v;
                                                                                        if (v === 'true') return true;
                                                                                        if (v === 'false' || v === '')
                                                                                                return false;
                                                                                        return Boolean(v);
                                                                                }
                                                                        }
                                                                )}
                                                        />
                                                </div>

                                                <div className={isSinglePanel ? 'flex items-start gap-2' : 'flex flex-col gap-4'}>
                                                        {/* Fields grid */}
                                                        <div
                                                                className={
                                                                        isSinglePanel
                                                                                ? 'flex-1 min-w-0'
                                                                                : 'grid flex-1 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'
                                                                }
                                                        >
                                                                {fields.map(fieldConfig => (
                                                                        <div
                                                                                key={fieldConfig.name}
                                                                                className={isSinglePanel ? '' : fieldConfig.colSpan}
                                                                        >
                                                                                {fieldConfig.render({
                                                                                        form,
                                                                                        path,
                                                                                        index,
                                                                                        itemValue: state,
                                                                                        isNewItem: !isInitialized,
                                                                                        onRemove: () => handleRemove(index)
                                                                                })}
                                                                        </div>
                                                                ))}
                                                        </div>
                                                        {/* Actions row */}
                                                        <div
                                                                className={
                                                                        isSinglePanel
                                                                                ? 'flex items-center gap-1 shrink-0'
                                                                                : 'flex items-center justify-end gap-2 pt-2 border-t border-border/50'
                                                                }
                                                        >
                                                                {renderHeaderActions?.({
                                                                        form,
                                                                        path,
                                                                        index,
                                                                        isInitialized
                                                                })}
                                                                {!hideDeleteButton && (
                                                                        <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => handleRemove(index)}
                                                                                className={isSinglePanel ? 'h-9 w-9 p-0 text-muted-foreground hover:text-destructive' : 'h-8 text-muted-foreground hover:text-destructive'}
                                                                        >
                                                                                <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                )}
                                                        </div>
                                                </div>
                                        </motion.div>
                                );
                        })}
                </AnimatePresence>
        );

        return (
                <div
                        className={`${isSinglePanel ? 'space-y-3' : 'space-y-6'} ${className}`}
                >
                        <div className="flex flex-col gap-2">
                                <div className="flex items-baseline gap-3">
                                        <h3 className="text-lg font-semibold">{title}</h3>
                                        {visibleItems.length > 0 && (
                                                <button
                                                        type="button"
                                                        onClick={handleClearAll}
                                                        className="text-sm font-normal text-muted-foreground/60 hover:text-destructive transition-colors"
                                                >
                                                        Clear all
                                                </button>
                                        )}
                                </div>
                                {description && (
                                        <p className="text-sm text-muted-foreground">{description}</p>
                                )}
                        </div>

                        <div className={itemsContainerClassName}>
                                {isSinglePanelInlineWrap ? (
                                        <div className={inlineWrapItemsClassName}>
                                                {visibleItems.length === 0 && emptyMessage && (
                                                        <p className="w-full px-2 py-1 text-sm text-muted-foreground">
                                                                {emptyMessage}
                                                        </p>
                                                )}
                                                {itemsContent}
                                                <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={handleAdd}
                                                        className={`justify-start flex-none gap-2 px-3 py-2 h-9 border border-dashed border-muted-foreground/50 text-primary hover:border-primary/50 hover:bg-muted/40 ${singlePanelItemWidthClassName}`}
                                                >
                                                        {addButtonLabel}
                                                </Button>
                                        </div>
                                ) : (
                                        <>
                                                {visibleItems.length === 0 && emptyMessage && (
                                                        <p className={`text-sm text-muted-foreground ${isSinglePanel ? 'px-4 py-4' : ''}`}>
                                                                {emptyMessage}
                                                        </p>
                                                )}
                                                {itemsContent}
                                                {isSinglePanel ? (
                                                        <Button
                                                                type="button"
                                                                variant="ghost"
                                                                onClick={handleAdd}
                                                                className="justify-start w-full gap-2 px-3 py-2 border-t rounded-none border-border/50 text-primary hover:bg-muted/40"
                                                        >
                                                                {addButtonLabel}
                                                        </Button>
                                                ) : (
                                                        <Button
                                                                type="button"
                                                                variant="ghost"
                                                                onClick={handleAdd}
                                                                className="justify-center w-full gap-2 py-3 bg-transparent border border-dashed border-muted-foreground/50 text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-transparent"
                                                        >
                                                                {addButtonLabel}
                                                        </Button>
                                                )}
                                        </>
                                )}
                        </div>
                </div>
        );
}
